import {Box, Button, IconButton, Paper, TextField} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {Dispatch, SetStateAction, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {IngredientType} from '../../../models/IngredientType';
import {IngredientInput} from "./IngredientInput";

const ingredientTypeName: string = 'Ingredient';
const groupTypeName: string = 'Group';

export type IngredientsPageProps = {
    ingredients: IngredientType[];
    updateIngredients: Dispatch<SetStateAction<IngredientType[]>>;
    allowGroupAdding: boolean;
    simplifiedEdit: boolean;
};

const IngredientEdit = (props: IngredientsPageProps) => {
    const {t} = useTranslation();

    const handleOnDragEnd = (result: any) => {
        if (!result.destination) return;

        const newIngredients: IngredientType[] = Array.from(props.ingredients);
        const [reorderedItem] = newIngredients.splice(result.source.index, 1);
        newIngredients.splice(result.destination.index, 0, reorderedItem);
        props.updateIngredients(newIngredients);
    };

    const changeAmount = useCallback((index: number, amount: number) => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients: IngredientType[] = [...currentIngredients];
            const changedIngredient: IngredientType | undefined = newIngredients[index];
            changedIngredient.amount = amount;
            newIngredients[index] = changedIngredient;
            return newIngredients;
        });
    }, [props]);

    const changeUnit = useCallback((index: number, unit: string) => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients: IngredientType[] = [...currentIngredients];
            const changedIngredient: IngredientType | undefined = newIngredients[index];
            changedIngredient.unit = unit;
            newIngredients[index] = changedIngredient;
            return newIngredients;
        });
    }, [props]);

    const changeIngredientName = useCallback((index: number, name: string) => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients: IngredientType[] = [...currentIngredients];
            const changedIngredient: IngredientType | undefined = newIngredients[index];
            changedIngredient.name = name;
            newIngredients[index] = changedIngredient;
            return newIngredients;
        });
    }, [props]);

    const addNewIngredient = useCallback(() => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients = [...currentIngredients];
            const key: number = newIngredients.length === 0 ? 0 : Math.max(...newIngredients.map(i => i.key)) + 1;
            newIngredients.push({id: null, name: '', amount: undefined, unit: undefined, key: key, type: ingredientTypeName});
            return newIngredients;
        });
    }, [props]);

    const addNewGroup = useCallback(() => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients = [...currentIngredients];
            const key: number = newIngredients.length === 0 ? 0 : Math.max(...newIngredients.map(i => i.key)) + 1;
            newIngredients.push({id: null, name: '', amount: undefined, unit: undefined, key: key, type: groupTypeName});
            return newIngredients;
        });
    }, [props])

    const deleteIngredient = useCallback((index: number) => {
        props.updateIngredients((currentIngredients: IngredientType[]) => {
            const newIngredients: IngredientType[] = [...currentIngredients];
            newIngredients.splice(index, 1);
            return newIngredients;
        });
    }, [props]);

    return (
        <div style={{height: 'calc(100% - 150px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="fields">
                {(provided) => (
                    <Box
                    component="div"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ maxWidth: '800px', paddingTop: 2 }}
                    >
                    {props.ingredients.map((ingredient, index) => (
                        <Draggable key={ingredient.key} draggableId={'' + ingredient.key} index={index}>
                        {(provided) => (
                            <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{ marginBottom: 1, padding: 2, display: 'flex', flexDirection: 'row', alignItems: 'center'}}
                            >
                                <DragHandleIcon fontSize="medium" style={{marginRight: '10px'}} />
                                {ingredient.type === 'Ingredient' && (
                                    <IngredientInput
                                        ingredient={ingredient}
                                        changeAmount={amount => changeAmount(index, amount)}
                                        changeUnit={unit => changeUnit(index, unit)}
                                        changeIngredientName={ingredientName => changeIngredientName(index, ingredientName)}
                                        simplifiedEdit={props.simplifiedEdit}
                                    />
                                )}
                                {ingredient.type === 'Group' && (
                                    <div style={{width: '100%'}}>
                                        <TextField
                                            id="category-name"
                                            label={<Box display={'flex'} flexDirection={'row'} alignItems={'center'}><div style={{marginLeft: '5px'}}>{t('group')}</div></Box>}
                                            variant="outlined"
                                            sx={{width: '100%'}}
                                            value={ingredient.name ? ingredient.name : ''}
                                            onChange={(e) => changeIngredientName(index, e.target.value)}
                                        />
                                    </div>
                                )}
                                <IconButton aria-label="delete" size="medium" onClick={() => deleteIngredient(index)}>
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </Paper>
                        )}
                        </Draggable>
                    ))}
                    {provided.placeholder}
                    </Box>
                )}
                </Droppable>
            </DragDropContext>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '10px'}}>
                <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addNewIngredient}>{t('newIngredient')}</Button>
                {props.allowGroupAdding && (
                    <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} style={{marginLeft: '5px'}} onClick={addNewGroup}>{t('newGroup')}</Button>
                )}
            </div>
        </div>
    );
};

export default IngredientEdit;